import api from './api';

class ComplianceService {
  static async exportReport(frameworkId = null) {
    try {
      // Use the existing 'api' instance instead of fetch
      const response = await api.get('/compliance/export', {
        params: frameworkId ? { frameworkId } : {},
        responseType: 'blob' // <--- Crucial: Tells axios to handle this as a file
      });

      // Create a download link for the blob data
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance_report_${new Date().toISOString().split('T')[0]}.csv`);
      
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Error exporting report:', error);
      throw this.handleError(error);
    }
  }
  
  // Get all compliance records with pagination and filtering
  static async getCompliances(params = {}) {
    try {
      const response = await api.get('/compliance', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching compliance records:', error);
      throw this.handleError(error);
    }
  }

  // Get compliance record by ID
  static async getCompliance(id) {
    try {
      const response = await api.get(`/compliance/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching compliance record:', error);
      throw this.handleError(error);
    }
  }

  // Create new compliance record
  static async createCompliance(complianceData) {
    try {
      const response = await api.post('/compliance', complianceData);
      return response.data;
    } catch (error) {
      console.error('Error creating compliance record:', error);
      throw this.handleError(error);
    }
  }

  // Update compliance record
  static async updateCompliance(id, complianceData) {
    try {
      const response = await api.put(`/compliance/${id}`, complianceData);
      return response.data;
    } catch (error) {
      console.error('Error updating compliance record:', error);
      throw this.handleError(error);
    }
  }

  // Delete compliance record
  static async deleteCompliance(id) {
    try {
      const response = await api.delete(`/compliance/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting compliance record:', error);
      throw this.handleError(error);
    }
  }

  // --- NEW METHODS FOR REGULATORY FRAMEWORKS ---

  // Get all regulatory frameworks
  static async getFrameworks() {
    try {
      // Matches backend route: /api/v1/compliance/frameworks
      const response = await api.get('/compliance/frameworks'); 
      return response.data;
    } catch (error) {
      console.error('Error fetching frameworks:', error);
      throw this.handleError(error);
    }
  }

  // Create a new regulatory framework
  static async createFramework(data) {
    try {
      const response = await api.post('/compliance/frameworks', data);
      return response.data;
    } catch (error) {
      console.error('Error creating framework:', error);
      throw this.handleError(error);
    }
  }

  static async deleteFramework(id) {
    try {
      const response = await api.delete(`/compliance/frameworks/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting framework:', error);
      throw this.handleError(error);
    }
  }
  
  static async updateFramework(id, data) {
    try {
      const response = await api.put(`/compliance/frameworks/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating framework:', error);
      throw this.handleError(error);
    }
  }

  // Add a specific requirement/control to a framework
  static async addRequirement(frameworkId, data) {
    try {
      const response = await api.post(`/compliance/frameworks/${frameworkId}/requirements`, data);
      return response.data;
    } catch (error) {
      console.error('Error adding requirement:', error);
      throw this.handleError(error);
    }
  }

  // --- END NEW METHODS ---

  // Handle API errors
  static handleError(error) {
    if (error.response) {
      // Server responded with error status
      const errorData = error.response.data;
      const errorMessage = errorData?.message || 
                           errorData?.error || 
                           (errorData?.errors && errorData.errors.length > 0 
                             ? errorData.errors.map(e => e.msg || e.message).join(', ') 
                             : 'An error occurred');
      
      return {
        message: errorMessage,
        status: error.response.status,
        data: errorData
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'Network error - please check your connection',
        status: 0
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        status: 0
      };
    }
  }
}

export default ComplianceService;